import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Modal, Form, Input, Select,
  DatePicker, Typography, Row, Col, Statistic, Popconfirm, Progress,
  Tooltip, message, Spin, Alert,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, BarChartOutlined, BellOutlined,
  ReloadOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { pollsApi, Poll, PollCreatePayload, PollStats } from '../../api/polls';
import { DistrictSelect } from '../../components/shared';

const { Title, Text } = Typography;
const { TextArea } = Input;

const PollsList: React.FC = () => {
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [statsOpen, setStatsOpen] = useState(false);
  const [statsData, setStatsData] = useState<PollStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [messageApi, contextHolder] = message.useMessage();

  const fetchPolls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pollsApi.list();
      setPolls(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPolls(); }, [fetchPolls]);

  const handleCreate = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }

    setSubmitting(true);
    try {
      const optionsArr = values.options
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean)
        .map((text: string) => ({ text_ru: text, text_kk: text }));

      if (optionsArr.length < 2) {
        messageApi.error('Нужно минимум 2 варианта ответа');
        setSubmitting(false);
        return;
      }

      const payload: PollCreatePayload = {
        title_ru: values.title_ru,
        title_kk: values.title_kk || values.title_ru,
        description_ru: values.description_ru,
        description_kk: values.description_kk,
        poll_type: values.poll_type || 'single',
        target_district: values.target_district,
        start_date: values.dates?.[0]?.toISOString(),
        end_date: values.dates?.[1]?.toISOString(),
        options: optionsArr,
      };

      await pollsApi.create(payload);
      messageApi.success('Опрос создан');
      setCreateOpen(false);
      form.resetFields();
      fetchPolls();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка создания');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await pollsApi.delete(id);
      messageApi.success('Опрос деактивирован');
      fetchPolls();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    }
  };

  const handleNotify = async (id: number) => {
    try {
      await pollsApi.notify(id);
      messageApi.success('Push-уведомление отправлено');
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка отправки');
    }
  };

  const openStats = async (id: number) => {
    setStatsOpen(true);
    setStatsLoading(true);
    try {
      const res = await pollsApi.stats(id);
      setStatsData(res.data);
    } catch {
      messageApi.error('Ошибка загрузки статистики');
    } finally {
      setStatsLoading(false);
    }
  };

  const activePolls = polls.filter(p => p.is_active).length;
  const totalVotes = polls.reduce((s, p) => s + p.total_votes, 0);
  const totalVoters = polls.reduce((s, p) => s + p.unique_voters, 0);

  const columns: ColumnsType<Poll> = [
    {
      title: 'Название',
      dataIndex: 'title_ru',
      key: 'title_ru',
      ellipsis: true,
      render: (title: string, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{title}</Text>
          {record.target_district && (
            <Text type="secondary" style={{ fontSize: 12 }}>{record.target_district}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'poll_type',
      key: 'poll_type',
      width: 120,
      render: (t: string) => (
        <Tag>
          {t === 'single' ? 'Один ответ' : 'Несколько'}
        </Tag>
      ),
    },
    {
      title: 'Голосов',
      key: 'votes',
      width: 120,
      render: (_: any, r: Poll) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.total_votes}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.unique_voters} чел.</Text>
        </Space>
      ),
    },
    {
      title: 'Вариантов',
      dataIndex: 'options_count',
      key: 'options_count',
      width: 100,
      align: 'center',
    },
    {
      title: 'Сроки',
      key: 'dates',
      width: 180,
      render: (_: any, r: Poll) => (
        <Space direction="vertical" size={0}>
          {r.start_date && <Text style={{ fontSize: 12 }}>{dayjs(r.start_date).format('DD.MM.YYYY')}</Text>}
          {r.end_date && <Text type="secondary" style={{ fontSize: 12 }}>до {dayjs(r.end_date).format('DD.MM.YYYY')}</Text>}
          {!r.start_date && !r.end_date && <Text type="secondary">—</Text>}
        </Space>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 130,
      render: (_: any, r: Poll) => {
        if (r.is_implemented) return <Tag icon={<CheckCircleOutlined />} color="success">Реализован</Tag>;
        if (r.is_active) return <Tag color="green">Активный</Tag>;
        return <Tag color="default">Завершён</Tag>;
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 140,
      render: (_: any, record: Poll) => (
        <Space size={4}>
          <Tooltip title="Статистика">
            <Button size="small" icon={<BarChartOutlined />} onClick={() => openStats(record.id)} />
          </Tooltip>
          <Tooltip title="Push-уведомление">
            <Button size="small" icon={<BellOutlined />} onClick={() => handleNotify(record.id)} disabled={!record.is_active} />
          </Tooltip>
          <Popconfirm title="Деактивировать опрос?" onConfirm={() => handleDelete(record.id)} okText="Да" cancelText="Нет">
            <Tooltip title="Деактивировать">
              <Button size="small" icon={<DeleteOutlined />} danger disabled={!record.is_active} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Опросы</Title>
          <Text type="secondary">Создание опросов для жителей города и статистика голосования</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setCreateOpen(true); }} style={{ background: 'var(--l-accent)' }}>
          Создать опрос
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Всего опросов" value={polls.length} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Активных" value={activePolls} valueStyle={{ color: 'var(--l-accent)' }} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Всего голосов" value={totalVotes} suffix={<Text type="secondary" style={{ fontSize: 14 }}>/ {totalVoters} чел.</Text>} /></Card>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Card bodyStyle={{ padding: 0 }} extra={<Button icon={<ReloadOutlined />} onClick={fetchPolls}>Обновить</Button>}>
        <Table<Poll> rowKey="id" columns={columns} dataSource={polls} loading={loading} pagination={{ pageSize: 20, showTotal: t => `Всего: ${t}` }} locale={{ emptyText: 'Опросы не найдены' }} scroll={{ x: 900 }} />
      </Card>

      {/* Create Modal */}
      <Modal title="Создать опрос" open={createOpen} onCancel={() => setCreateOpen(false)} onOk={handleCreate} confirmLoading={submitting} okText="Создать" cancelText="Отмена" width={640} destroyOnClose>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title_ru" label="Название (рус.)" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Например: Какой парк благоустроить первым?" />
          </Form.Item>
          <Form.Item name="title_kk" label="Название (каз.)">
            <Input placeholder="Қай саябақты алдымен абаттандыру керек?" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="description_ru" label="Описание (рус.)">
                <TextArea rows={2} placeholder="Описание опроса" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="description_kk" label="Сипаттамасы (каз.)">
                <TextArea rows={2} placeholder="Сауалнама сипаттамасы" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="options" label="Варианты ответов (каждый на новой строке)" rules={[{ required: true, message: 'Введите варианты' }]} extra="Минимум 2 варианта">
            <TextArea rows={4} placeholder={"Парк на ул. Ленина\nСквер на пр. Абая\nДетская площадка в мкр.3"} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="poll_type" label="Тип" initialValue="single">
                <Select>
                  <Select.Option value="single">Один ответ</Select.Option>
                  <Select.Option value="multiple">Несколько</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="target_district" label="Район">
                <DistrictSelect placeholder="Все районы" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dates" label="Период">
                <DatePicker.RangePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Stats Modal */}
      <Modal title="Статистика опроса" open={statsOpen} onCancel={() => { setStatsOpen(false); setStatsData(null); }} footer={null} width={600}>
        <Spin spinning={statsLoading}>
          {statsData && (
            <div>
              <Title level={5}>{statsData.title_ru}</Title>
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}><Statistic title="Всего голосов" value={statsData.total_votes} /></Col>
                <Col span={12}><Statistic title="Уникальных" value={statsData.unique_voters} /></Col>
              </Row>
              <div>
                {statsData.options.map((opt) => (
                  <div key={opt.id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Text>{opt.text_ru}</Text>
                      <Text strong>{opt.vote_count} ({opt.percentage}%)</Text>
                    </div>
                    <Progress percent={opt.percentage || 0} showInfo={false} strokeColor="var(--l-accent)" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default PollsList;
